// =============================================================================
// Client-side API for shared fuel logs ("Tank Liste").
//
// Schema (see supabase/schema.sql section 9):
//   fuel_logs         (id, name, owner_id, currency, created_at)
//   fuel_log_members  (log_id, user_id, joined_at)
//   fuel_log_invites  (token, log_id, created_by, created_at)
//   fuel_entries      (id, log_id, user_id, entry_date, odometer,
//                      fuel_cost, liters, note, created_at)
//
// Settlement model
// ----------------
// Entries are sorted by `odometer` ascending (the car's odometer is
// strictly monotonic in real life). The km between two consecutive entries
// is attributed to whoever logged the *later* entry — convention: you log
// when you finish driving.
//
// A "fill-up" is any entry with `fuel_cost > 0`. When a fill-up happens,
// the cost is split among drivers proportionally to the km each person
// drove since the previous fill-up (or since the very first entry, if no
// previous fill-up exists). The fill-up payer paid the whole bill, so the
// other drivers owe their share to the payer.
//
// `computeBalances(entries)` returns:
//   {
//     totals: { [userId]: { km, paid, owesNet } },
//     // owesNet > 0 = this user is owed money; < 0 = they owe.
//     // When two users net out the diff is the amount one owes the other.
//   }
// =============================================================================
import { supabase } from './supabase';

const buildInviteUrl = (token) => {
  const { origin, pathname } = window.location;
  const base = `${origin}${pathname.replace(/\/+$/, '')}`;
  return `${base}?joinFuelLog=${encodeURIComponent(token)}`;
};

// -------- Logs ---------------------------------------------------------------

// Returns [{ id, name, currency, ownerId, isOwner, createdAt }].
export async function listFuelLogs() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('fuel_logs')
    .select('id, name, currency, owner_id, created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    currency: r.currency || 'EUR',
    ownerId: r.owner_id,
    isOwner: r.owner_id === user.id,
    createdAt: r.created_at
  }));
}

// Create a log; the RPC also adds the creator as a member and provisions
// a reusable invite token. Returns { id, name, currency, inviteToken }.
export async function createFuelLog(name, currency = 'EUR') {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name is required');
  const { data, error } = await supabase.rpc('create_fuel_log', {
    log_name: trimmed,
    log_currency: currency || 'EUR'
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No row returned from create_fuel_log');
  return {
    id: row.id,
    name: row.name,
    currency: row.currency || 'EUR',
    inviteToken: row.invite_token
  };
}

// Rename / change currency. Owner-only (RLS enforced).
export async function updateFuelLog(logId, patch) {
  const row = {};
  if (patch.name !== undefined) {
    const t = (patch.name || '').trim();
    if (!t) throw new Error('Name is required');
    row.name = t;
  }
  if (patch.currency !== undefined) {
    row.currency = (patch.currency || 'EUR').toUpperCase().slice(0, 8);
  }
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('fuel_logs')
    .update(row)
    .eq('id', logId);
  if (error) throw error;
}

// Delete (owner only). Cascades to members, invites, entries.
export async function deleteFuelLog(logId) {
  const { error } = await supabase.from('fuel_logs').delete().eq('id', logId);
  if (error) throw error;
}

// -------- Invites ------------------------------------------------------------

export async function getFuelLogInviteUrl(logId) {
  const { data, error } = await supabase
    .from('fuel_log_invites')
    .select('token')
    .eq('log_id', logId)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  if (data) return buildInviteUrl(data.token);
  // No token yet — owners can create one (RLS enforces ownership).
  const { data: { user } } = await supabase.auth.getUser();
  // Use the same compact base36 random scheme as shared_calendars (so the
  // token shape is consistent across resources).
  const arr = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  const token = Array.from(arr, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 28);
  const { error: insErr } = await supabase
    .from('fuel_log_invites')
    .insert({ token, log_id: logId, created_by: user.id });
  if (insErr) throw insErr;
  return buildInviteUrl(token);
}

export async function joinFuelLogByToken(token) {
  const { data, error } = await supabase.rpc('join_fuel_log', {
    invite_token: token
  });
  if (error) throw error;
  return data; // uuid
}

// -------- Members ------------------------------------------------------------

export async function listFuelLogMembers(logId) {
  const { data, error } = await supabase.rpc('list_fuel_log_members', {
    lid: logId
  });
  if (error) throw error;
  return (data || []).map((r) => ({
    userId: r.user_id,
    email: r.email,
    displayName: r.display_name,
    avatarEmoji: r.avatar_emoji,
    avatarColor: r.avatar_color,
    avatarUrl: r.avatar_url,
    avatarKind: r.avatar_kind || 'emoji',
    isOwner: r.is_owner,
    joinedAt: r.joined_at
  }));
}

export async function leaveFuelLog(logId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase
    .from('fuel_log_members')
    .delete()
    .eq('log_id', logId)
    .eq('user_id', user.id);
  if (error) throw error;
}

// -------- Entries ------------------------------------------------------------

// Returns entries sorted by odometer ascending (the natural settlement
// order). Caller can reverse for display if desired.
export async function listFuelEntries(logId) {
  const { data, error } = await supabase
    .from('fuel_entries')
    .select('id, log_id, user_id, entry_date, odometer, start_odometer, fuel_cost, liters, note, created_at')
    .eq('log_id', logId)
    .order('odometer', { ascending: true })
    .order('entry_date', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    logId: r.log_id,
    userId: r.user_id,
    entryDate: r.entry_date,
    odometer: r.odometer,
    startOdometer: r.start_odometer == null ? null : Number(r.start_odometer),
    fuelCost: r.fuel_cost == null ? null : Number(r.fuel_cost),
    liters: r.liters == null ? null : Number(r.liters),
    note: r.note || '',
    createdAt: r.created_at
  }));
}

// `entry.userId` is optional — pass it to log on behalf of another
// member (e.g. Jana enters an odometer reading for Petra). Defaults to
// the currently signed-in user. RLS guarantees the named user is a
// member of the log.
export async function addFuelEntry(logId, entry) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const row = {
    log_id: logId,
    user_id: entry.userId || user.id,
    entry_date: entry.entryDate,
    odometer: Math.round(Number(entry.odometer)),
    start_odometer:
      entry.startOdometer == null || entry.startOdometer === ''
        ? null
        : Math.round(Number(entry.startOdometer)),
    fuel_cost: entry.fuelCost == null || entry.fuelCost === ''
      ? null
      : Number(entry.fuelCost),
    liters: entry.liters == null || entry.liters === ''
      ? null
      : Number(entry.liters),
    note: (entry.note || '').trim() || null
  };
  if (!row.entry_date) throw new Error('Datum fehlt');
  if (!Number.isFinite(row.odometer) || row.odometer < 0)
    throw new Error('Kilometerstand fehlt');
  if (
    row.start_odometer != null &&
    (row.start_odometer < 0 || row.start_odometer > row.odometer)
  ) {
    throw new Error('Start-km muss kleiner als End-km sein');
  }

  const { data, error } = await supabase
    .from('fuel_entries')
    .insert(row)
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function deleteFuelEntry(entryId) {
  const { error } = await supabase
    .from('fuel_entries')
    .delete()
    .eq('id', entryId);
  if (error) throw error;
}

// -------- Settlements (manual "marked as paid" rows) -----------------------

export async function listFuelSettlements(logId) {
  const { data, error } = await supabase
    .from('fuel_settlements')
    .select('id, log_id, from_user_id, to_user_id, amount, settled_at, created_by, note')
    .eq('log_id', logId)
    .order('settled_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    logId: r.log_id,
    fromUserId: r.from_user_id,
    toUserId: r.to_user_id,
    amount: Number(r.amount),
    settledAt: r.settled_at,
    createdBy: r.created_by,
    note: r.note || ''
  }));
}

export async function addFuelSettlement(logId, { fromUserId, toUserId, amount, note }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  if (!fromUserId || !toUserId) throw new Error('Beide Beteiligte sind nötig');
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Betrag ungültig');
  const { data, error } = await supabase
    .from('fuel_settlements')
    .insert({
      log_id: logId,
      from_user_id: fromUserId,
      to_user_id: toUserId,
      amount: amt,
      created_by: user.id,
      note: (note || '').trim() || null
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function deleteFuelSettlement(settlementId) {
  const { error } = await supabase
    .from('fuel_settlements')
    .delete()
    .eq('id', settlementId);
  if (error) throw error;
}

// -------- Settlement math ----------------------------------------------------

/**
 * Given an array of entries (sorted by odometer asc), compute settlement.
 *
 * The "from" km of each entry is:
 *   - `entry.startOdometer` if set (explicit baseline, e.g. on the very
 *     first entry of a log), OR
 *   - the previous entry's odometer (the natural chain), OR
 *   - the entry's own odometer if it's first AND has no startOdometer
 *     (so it contributes 0 km — unknown baseline).
 *
 * Returns:
 *   {
 *     perUser: { [userId]: { km, paid, owed } },
 *     // `owed` > 0 → user is owed money; < 0 → user owes.
 *     pairwise:  [{ from, to, amount }],  // condensed 2-user-friendly settlement
 *     perFillUp: [{ entryId, payerId, totalCost, costByUser: { uid: amt }, totalKm }],
 *     fillUpCount, totalKm, unsettledKm
 *   }
 */
export function computeBalances(entries, settlements = []) {
  const sorted = [...entries].sort((a, b) =>
    a.odometer === b.odometer
      ? String(a.entryDate).localeCompare(String(b.entryDate))
      : a.odometer - b.odometer
  );

  // Per-entry "from" km — null only when it'd make the entry contribute 0.
  const fromKm = sorted.map((e, i) => {
    if (e.startOdometer != null) return e.startOdometer;
    if (i > 0) return sorted[i - 1].odometer;
    return e.odometer; // first entry, no explicit start → 0 km
  });

  const kmFor = (i) => Math.max(0, sorted[i].odometer - fromKm[i]);

  const perUser = {};
  const ensure = (uid) => {
    if (!perUser[uid]) perUser[uid] = { km: 0, paid: 0, owed: 0 };
    return perUser[uid];
  };

  // First pass: km attribution. Each entry's km belongs to its author.
  for (let i = 0; i < sorted.length; i++) {
    ensure(sorted[i].userId).km += kmFor(i);
  }

  // Second pass: per-fill-up settlement.
  const perFillUp = [];
  let lastFillUpIdx = -1;
  for (let i = 0; i < sorted.length; i++) {
    const e = sorted[i];
    if (e.fuelCost == null || e.fuelCost <= 0) continue;

    // Window = entries (lastFillUpIdx, i].
    const windowStart = lastFillUpIdx + 1;
    const windowKmByUser = {};
    let windowKm = 0;
    for (let j = windowStart; j <= i; j++) {
      const km = kmFor(j);
      windowKm += km;
      windowKmByUser[sorted[j].userId] =
        (windowKmByUser[sorted[j].userId] || 0) + km;
    }

    // Payer paid the full bill; each driver's share is proportional to km.
    ensure(e.userId).paid += e.fuelCost;
    const costByUser = {};
    if (windowKm > 0) {
      for (const [uid, km] of Object.entries(windowKmByUser)) {
        const share = e.fuelCost * (km / windowKm);
        costByUser[uid] = share;
        ensure(uid).owed -= share;
      }
    } else {
      // No km recorded in this window → fall back to splitting equally
      // among everyone who has driven in the log so far.
      const drivers = Object.keys(perUser);
      if (drivers.length > 0) {
        const share = e.fuelCost / drivers.length;
        for (const uid of drivers) {
          costByUser[uid] = share;
          ensure(uid).owed -= share;
        }
      }
    }
    ensure(e.userId).owed += e.fuelCost;

    perFillUp.push({
      entryId: e.id,
      payerId: e.userId,
      totalCost: e.fuelCost,
      costByUser,
      totalKm: windowKm
    });
    lastFillUpIdx = i;
  }

  const fillUpCount = perFillUp.length;

  // Total km logged.
  let totalKm = 0;
  for (let i = 0; i < sorted.length; i++) totalKm += kmFor(i);

  // Km after the last fill-up not yet billed.
  let unsettledKm = 0;
  for (let i = lastFillUpIdx + 1; i < sorted.length; i++) {
    unsettledKm += kmFor(i);
  }

  // Apply manual settlements ("Jana paid Petra €X"). Each settlement
  // increases the debtor's `owed` (they paid back, so they owe less)
  // and decreases the creditor's `owed` (they got paid, so they're owed
  // less). Total settled tracked for UI display.
  let totalSettled = 0;
  for (const s of settlements) {
    totalSettled += s.amount;
    if (!perUser[s.fromUserId]) perUser[s.fromUserId] = { km: 0, paid: 0, owed: 0 };
    if (!perUser[s.toUserId]) perUser[s.toUserId] = { km: 0, paid: 0, owed: 0 };
    perUser[s.fromUserId].owed += s.amount;
    perUser[s.toUserId].owed -= s.amount;
  }

  // For the common 2-user case, summarise into a single "X owes Y €Z" arrow.
  const userIds = Object.keys(perUser);
  const pairwise = [];
  if (userIds.length === 2) {
    const [a, b] = userIds;
    const aOwed = perUser[a].owed;
    const bOwed = perUser[b].owed;
    // Whoever has negative owed amount owes the other the absolute value.
    if (aOwed < -0.005) {
      pairwise.push({ from: a, to: b, amount: -aOwed });
    } else if (bOwed < -0.005) {
      pairwise.push({ from: b, to: a, amount: -bOwed });
    }
  } else if (userIds.length > 2) {
    // Naive multi-user settlement: each negative-balance user owes the
    // most-positive user, up to either's amount. Iterate.
    const debts = userIds.map((u) => ({ u, v: perUser[u].owed }));
    while (true) {
      debts.sort((x, y) => x.v - y.v);
      const debtor = debts[0];
      const creditor = debts[debts.length - 1];
      if (debtor.v >= -0.005 || creditor.v <= 0.005) break;
      const amt = Math.min(-debtor.v, creditor.v);
      pairwise.push({ from: debtor.u, to: creditor.u, amount: amt });
      debtor.v += amt;
      creditor.v -= amt;
    }
  }

  return { perUser, pairwise, perFillUp, fillUpCount, totalKm, unsettledKm, totalSettled };
}
